import React, { useState, useEffect } from "react";
import { Shield, CheckCircle2, ChevronRight, XCircle } from "lucide-react";

export default function KnowledgeCheckGateway({ question, options, onNext, isDark }) {
  const [selected, setSelected]   = useState(null);
  const [isPassed, setIsPassed]   = useState(false);
  const [shakeId,  setShakeId]    = useState(null);
  const [attempts, setAttempts]   = useState(0);
  const D = isDark;

  useEffect(() => {
    setSelected(null);
    setIsPassed(false);
    setShakeId(null);
    setAttempts(0);
  }, [question]);

  const handleSelect = (option) => {
    if (isPassed) return;
    setSelected(option.id);
    setAttempts(a => a + 1);

    if (option.isCorrect) {
      setIsPassed(true);
    } else {
      setShakeId(option.id);
      setTimeout(() => setShakeId(null), 500);
    }
  };

  const panel   = D ? "#142a4d" : "#ffffff";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text    = D ? "#f0f4f8" : "#0d1f3c";
  const textMid = D ? "rgba(240,244,248,0.80)" : "#334155";
  const textSub = D ? "rgba(240,244,248,0.55)" : "#64748b";
  const mid     = D ? "#1a3358" : "#f0f4f8";
  const gold    = "#c9971c";
  const goldL   = "#fbbf24";

  const optionStyle = (option) => {
    const isSelected  = selected === option.id;
    const isWrong     = isSelected && !option.isCorrect;
    const showCorrect = isPassed && option.isCorrect;
    const shake       = shakeId === option.id;

    let bg    = D ? "rgba(255,255,255,0.04)" : "#fafafa";
    let bdr   = border;
    let color = textMid;

    if (showCorrect) { bg = D ? "rgba(5,150,105,0.15)" : "#ecfdf5"; bdr = D ? "rgba(52,211,153,0.40)" : "#6ee7b7"; color = D ? "#34d399" : "#065f46"; }
    else if (isWrong) { bg = D ? "rgba(220,38,38,0.12)" : "#fff1f2"; bdr = D ? "rgba(248,113,113,0.40)" : "#fca5a5"; color = D ? "#fca5a5" : "#991b1b"; }
    else if (isSelected) { bg = D ? "rgba(201,151,28,0.12)" : "#fffbeb"; bdr = "rgba(201,151,28,0.40)"; color = D ? goldL : "#92400e"; }

    return { bg, bdr, color, shake };
  };

  return (
    <div style={{ background: panel, borderRadius: 20, border: `1.5px solid ${border}`, padding: "clamp(20px,3vw,32px)", boxShadow: D ? "0 4px 24px rgba(0,0,0,0.28)" : "0 4px 24px rgba(13,31,60,0.10)" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: D ? "rgba(201,151,28,0.15)" : "#fef9ec", border: `1.5px solid rgba(201,151,28,0.30)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Shield size={20} style={{ color: gold }} />
        </div>
        <div>
          <div style={{ fontSize: "clamp(14px,2vw,18px)", fontWeight: 800, color: text }}>Mandatory Knowledge Check</div>
          <div style={{ fontSize: 12, color: textSub, marginTop: 2 }}>Answer correctly to unlock the next module</div>
        </div>
      </div>

      {/* Question */}
      <div style={{ background: mid, borderRadius: 13, padding: "clamp(14px,2vw,20px)", marginBottom: "clamp(16px,2vw,24px)", border: `1px solid ${border}` }}>
        <p style={{ fontSize: "clamp(14px,2vw,18px)", fontWeight: 700, color: text, margin: 0, lineHeight: 1.6 }}>
          {question}
        </p>
      </div>

      {/* Options */}
      <div style={{ display: "flex", flexDirection: "column", gap: "clamp(8px,1.5vw,12px)", marginBottom: isPassed ? "clamp(16px,2vw,24px)" : 0 }}>
        {options.map((option) => {
          const { bg, bdr, color, shake } = optionStyle(option);
          const showCorrect = isPassed && option.isCorrect;
          const isWrong     = selected === option.id && !option.isCorrect;

          return (
            <button key={option.id} onClick={() => handleSelect(option)}
              style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, padding: "clamp(12px,2vw,16px) clamp(14px,2vw,18px)",
                borderRadius: 13, border: `1.5px solid ${bdr}`,
                background: bg, color: color,
                cursor: isPassed ? "default" : "pointer",
                textAlign: "left", fontFamily: "inherit",
                transition: "all .15s",
                animation: shake ? "shake .4s ease" : "none",
                boxShadow: showCorrect ? `0 0 0 3px rgba(52,211,153,0.20)` : isWrong ? `0 0 0 3px rgba(248,113,113,0.15)` : "none",
              }}
              onMouseEnter={e => { if (!isPassed && selected !== option.id) e.currentTarget.style.borderColor = gold; }}
              onMouseLeave={e => { if (!isPassed && selected !== option.id) e.currentTarget.style.borderColor = border; }}
            >
              <span style={{ fontSize: "clamp(13px,1.8vw,16px)", fontWeight: 600, lineHeight: 1.5 }}>{option.text}</span>
              <span style={{ flexShrink: 0 }}>
                {showCorrect && <CheckCircle2 size={20} style={{ color: "#34d399" }} />}
                {isWrong     && <XCircle     size={20} style={{ color: "#f87171" }} />}
              </span>
            </button>
          );
        })}
      </div>

      {/* Wrong attempts hint */}
      {attempts > 1 && !isPassed && (
        <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10, background: D ? "rgba(201,151,28,0.10)" : "#fffbeb", border: `1px solid rgba(201,151,28,0.25)`, fontSize: 12, color: D ? goldL : "#92400e", fontWeight: 600 }}>
          💡 Hint: Think about the exact legal standard that applies uniformly to all operators in this group.
        </div>
      )}

      {/* Pass state */}
      {isPassed && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, paddingTop: "clamp(16px,2vw,22px)", borderTop: `1px solid ${border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CheckCircle2 size={18} style={{ color: "#34d399", flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: "clamp(13px,1.8vw,16px)", fontWeight: 800, color: D ? "#34d399" : "#059669" }}>Correct! Module Complete.</div>
              <div style={{ fontSize: 11, color: textSub, marginTop: 1 }}>You may now proceed to the next module.</div>
            </div>
          </div>
          <button onClick={onNext} style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "clamp(10px,1.5vw,13px) clamp(20px,2.5vw,28px)",
            borderRadius: 11, border: "none", cursor: "pointer",
            background: `linear-gradient(135deg,${gold},${goldL})`,
            color: "#0d1f3c", fontWeight: 800,
            fontSize: "clamp(13px,1.8vw,15px)", fontFamily: "inherit",
            boxShadow: `0 4px 14px rgba(201,151,28,0.45)`,
            transition: "all .15s", whiteSpace: "nowrap",
          }}
          onMouseEnter={e => { e.currentTarget.style.filter = "brightness(1.08)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={e => { e.currentTarget.style.filter = "none"; e.currentTarget.style.transform = "none"; }}>
            Next Module <ChevronRight size={16} />
          </button>
        </div>
      )}

      <style>{`
        @keyframes shake {
          0%,100%{transform:translateX(0)}
          20%{transform:translateX(-8px)}
          40%{transform:translateX(8px)}
          60%{transform:translateX(-5px)}
          80%{transform:translateX(5px)}
        }
      `}</style>
    </div>
  );
}
